import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";

const ARROW_BUTTON_BASE =
  "absolute z-10 flex items-center justify-center rounded-full bg-white/90 dark:bg-acs-dark-surface/90 shadow-md border border-gray-200 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text hover:bg-white dark:hover:bg-acs-dark-hover hover:text-[#223688] dark:hover:text-[#6DE1C2] transition-none h-7 w-7";

/**
 * @param {boolean} [showArrows=false] - Opt-in overlay prev/next buttons for when a caller's
 * content routinely overflows and a hidden (scrollbar-hide) native scrollbar leaves no visible
 * affordance that there's more to scroll to. Arrows only render while there's actually somewhere
 * left to scroll in that direction, and never disturb existing consumers that don't pass it.
 */
const ScrollableContainer = ({ children, className = "", direction = "x", showArrows = false }) => {
  const scrollRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateArrowState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (direction === "x") {
      setCanScrollPrev(el.scrollLeft > 4);
      setCanScrollNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    } else {
      setCanScrollPrev(el.scrollTop > 4);
      setCanScrollNext(el.scrollTop < el.scrollHeight - el.clientHeight - 4);
    }
  }, [direction]);

  useEffect(() => {
    if (!showArrows) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;

    updateArrowState();
    el.addEventListener("scroll", updateArrowState, { passive: true });
    // Content (e.g. widget counts loading in) or a container resize can change whether
    // there's anything left to scroll to, independent of the user ever scrolling at all.
    // Absent in older browsers and in jsdom; the window resize listener below still covers resizes.
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateArrowState);
    resizeObserver?.observe(el);
    window.addEventListener("resize", updateArrowState);

    return () => {
      el.removeEventListener("scroll", updateArrowState);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateArrowState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-check whenever the rendered
    // children change (e.g. widget count finishes loading), not just on scroll/resize.
  }, [showArrows, updateArrowState, children]);

  const scrollByStep = (stepDirection) => {
    const el = scrollRef.current;
    if (!el) return;
    if (direction === "x") {
      el.scrollBy({ left: stepDirection * el.clientWidth * 0.8, behavior: "smooth" });
    } else {
      el.scrollBy({ top: stepDirection * el.clientHeight * 0.8, behavior: "smooth" });
    }
  };

  const PrevIcon = direction === "x" ? ChevronLeft : ChevronUp;
  const NextIcon = direction === "x" ? ChevronRight : ChevronDown;

  return (
    <div className={showArrows ? "relative flex-1 min-w-0 min-h-0" : undefined}>
      {showArrows && canScrollPrev && (
        <button
          type="button"
          onClick={() => scrollByStep(-1)}
          aria-label={direction === "x" ? "Scroll left" : "Scroll up"}
          className={`${ARROW_BUTTON_BASE} ${
            direction === "x" ? "left-1 top-1/2 -translate-y-1/2" : "top-1 left-1/2 -translate-x-1/2"
          }`}
        >
          <PrevIcon size={16} />
        </button>
      )}

      <div
        ref={scrollRef}
        className={`w-full ${
          direction === "x"
            ? "overflow-x-scroll scrollbar-hide flex gap-fluid-1"
            : "overflow-y-auto scrollbar-hide space-y-fluid-1"
        } scroll-smooth scroll-snap-${
          direction === "x" ? "x" : "y"
        } mandatory p-fluid-1 ${className}`}
        style={{
          scrollPadding: direction === "x"
            ? '0 clamp(0.5rem, 1.0vw, 1rem)'
            : 'clamp(0.5rem, 1.0vw, 1rem) 0',
          scrollbarGutter: 'stable'
        }}
      >
        {React.Children.map(children, child =>
          React.cloneElement(child, {
            className: `${
              direction === "x"
                ? "flex-[0_0_auto] snap-start"
                : "snap-start"
            } ${child.props.className || ""}`
          })
        )}
      </div>

      {showArrows && canScrollNext && (
        <button
          type="button"
          onClick={() => scrollByStep(1)}
          aria-label={direction === "x" ? "Scroll right" : "Scroll down"}
          className={`${ARROW_BUTTON_BASE} ${
            direction === "x" ? "right-1 top-1/2 -translate-y-1/2" : "bottom-1 left-1/2 -translate-x-1/2"
          }`}
        >
          <NextIcon size={16} />
        </button>
      )}
    </div>
  );
};

export default ScrollableContainer;
